import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { bannerApiUrl } from "../utils";
import {validateImageDimensions} from "./ValidateBannerImage";

const initialForm = {
  title: "",
  description: "",
  ord: "",
  targetScreen: "",
  buttonText: "",
  buttonLink: "",
  largeImageFile: null,
  smallImageFile: null,
  expiryDate: null
};

export default function useBanner() {
  const _isMounted = useRef(false);

  const [bannerData, setBannerData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [noData, setNoData] = useState("");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(null);
  const PER = 20;

  // Modal state
  const [openModal, setOpenModal] = useState(false);
  const [mode, setMode] = useState("add"); // "add" | "edit"
  const [editBannerId, setEditBannerId] = useState(null);
  const [form, setForm] = useState(initialForm);

   const [imageErrors, setImageErrors] = useState({largeImageFile: "",smallImageFile: "",});

  useEffect(() => {
    _isMounted.current = true;
    fetchBanners();
    return () => {
      _isMounted.current = false;
    };
  }, []);

  const setField = (field, value) =>
    setForm((prev) => ({ ...prev, [field]: value }));
  const handleLargeImageChange = async (file) => {
  try {
    await validateImageDimensions(file, "WEB");

    setImageErrors((prev) => ({
      ...prev,
      largeImageFile: "",
    }));

    setField("largeImageFile", file);
  } catch (err) {
    setImageErrors((prev) => ({
      ...prev,
      largeImageFile: err.message,
    }));
  }
};

const handleSmallImageChange = async (file) => {
  try {
    await validateImageDimensions(file, "MOBILE");

    setImageErrors((prev) => ({
      ...prev,
      smallImageFile: "",
    }));

    setField("smallImageFile", file);
  } catch (err) {
    setImageErrors((prev) => ({
      ...prev,
      smallImageFile: err.message,
    }));
  }
};

  const fetchBanners = (isLoadMore = false) => {
    setLoading(true);
    setNoData("");
    const currentPage = isLoadMore ? page : 1;
    if (!isLoadMore) setBannerData([]);

    axios
      .get(
        bannerApiUrl(`api/v1/banner/getall?page=${currentPage}&limit=${PER}`),
      )
      .then(({ data }) => {
        if (!_isMounted.current) return;
        if (data.status && data.data) {
          setBannerData((prev) =>
            isLoadMore ? [...prev, ...data.data] : data.data,
          );
          setTotalPages(data.total_pages);
          setPage(currentPage + 1);
          setNoData(data.data.length === 0 ? "No Banner Found" : "");
        } else {
          setNoData("No Banner Found");
        }
      })
      .catch(() => setNoData("Error fetching data, please try again"))
      .finally(() => setLoading(false));
  };

  const submitBanner = async () => {
    try {
      const {
        title,
        description,
        ord,
        targetScreen,
        buttonText,
        buttonLink,
        largeImageFile,
        smallImageFile,
        expireAt
      } = form;

      const payload = {
        title,
        description,
        ord: Number(ord),
        targetScreen,
        buttonText,
        buttonLink,
        webImageS3Key: {
          mimeType: largeImageFile.type,
          fileName: largeImageFile.name,
        },
        mobileImageS3Key: {
          mimeType: smallImageFile.type,
          fileName: smallImageFile.name,
        },
        expireAt
      };

      const { data } = await axios.post(bannerApiUrl("api/v1/banner"), payload);
      console.log("data", data);
      const { webImageUrl, mobileImageUrl } = data.uploads;

      await axios.put(webImageUrl.uploadUrl, largeImageFile, {
        headers: { "Content-Type": largeImageFile.type },
      });
      await axios.put(mobileImageUrl.uploadUrl, smallImageFile, {
        headers: { "Content-Type": smallImageFile.type },
      });

      alert("Banner Added Successfully");
      closeModal();
      fetchBanners();
    } catch (err) {
      console.error(err);
      alert("Failed to add banner");
    }
  };

  const updateBanner = async () => {
    try {
      const { title, description, ord, targetScreen, buttonText, buttonLink ,expireAt} =
        form;

      await axios.put(bannerApiUrl(`api/v1/banner/${editBannerId}`), {
        title,
        description,
        ord: Number(ord),
        targetScreen,
        buttonText,
        buttonLink,
        expireAt
      });

      setBannerData((prev) =>
        prev.map((banner) =>
          banner.id === editBannerId
            ? {
                ...banner,
                title,
                description,
                ord: Number(ord),
                targetScreen,
                buttonText,
                buttonLink,
                updatedAt: new Date().toISOString(),
                expireAt
              }
            : banner,
        ),
      );

      alert("Banner updated successfully");
      closeModal();
    } catch (err) {
      console.error(err);
      alert("Failed to update banner");
    }
  };
  const onStatusChange = async (id) => {
    try {
      await axios.patch(bannerApiUrl(`api/v1/banner/change_status/${id}`));

      setBannerData((prev) =>
        prev.map((banner) =>
          banner.id === id
            ? {
                ...banner,
                status: !banner.status,
              }
            : banner,
        ),
      );
    } catch (error) {
      console.error(error);
    }
  };

  const deleteBanner = async (bannerId) => {
    try {
      await axios.delete(bannerApiUrl(`api/v1/banner/${bannerId}`));

      setBannerData((prev) => prev.filter((banner) => banner.id !== bannerId));

      alert("Banner deleted successfully");
    } catch (err) {
      console.error(err);
      alert("Failed to delete banner");
    }
  };

  const openAddModal = () => {
    setForm(initialForm);
    setMode("add");
    setEditBannerId(null);
    setOpenModal(true);
  };

  const openEditModal = (banner) => {
    setForm({
      ...initialForm,
      title: banner.title || "",
      description: banner.description || "",
      ord: banner.ord || "",
      targetScreen: banner.targetScreen || "",
      buttonText: banner.buttonText || "",
      buttonLink: banner.buttonLink || "",
      expireAt: banner.expireAt
  ? banner.expireAt.split("T")[0]
  : ""
    });
    setMode("edit");
    setEditBannerId(banner.id);
    setOpenModal(true);
  };

  const closeModal = () => {
    setOpenModal(false);
    setMode("add");
    setEditBannerId(null);
    setForm(initialForm);
  };

  return {
    bannerData,
    loading,
    noData,
    totalPages,
    openModal,
    mode,
    form,
    setField,
    fetchBanners,
    submitBanner,
    updateBanner,
    deleteBanner,
    openAddModal,
    openEditModal,
    closeModal,
    onStatusChange,
    imageErrors,
    handleLargeImageChange,
    handleSmallImageChange,
  };
}
